/**
 * Encryption Crypto Primitives
 *
 * Pure functions using the Web Crypto API for AES-256-GCM encryption/decryption
 * and HKDF key derivation. These are the low-level building blocks used by
 * the EncryptionService.
 *
 * IMPORTANT: The HKDF parameters here must exactly match the server-side
 * implementation in firebase-functions/src/index.ts for push notification
 * decryption to work correctly.
 */

import { ENCRYPTION_ALGORITHM, KEY_LENGTH, IV_LENGTH, HKDF_INFO, HKDF_HASH } from './constants';

// ==================== Base64 Helpers ====================

/** Encode an ArrayBuffer to a Base64 string */
export function base64Encode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Decode a Base64 string to an ArrayBuffer */
export function base64Decode(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/** Convert a hex string to an ArrayBuffer */
export function hexToArrayBuffer(hex: string): ArrayBuffer {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes.buffer;
}

// ==================== Key Management ====================

/**
 * Import a hex-encoded master key as a CryptoKey suitable for HKDF derivation.
 *
 * The master key is imported as raw key material for HKDF, NOT directly as
 * an AES key. Per-conversation keys are derived from it via deriveConversationKey().
 */
export async function importMasterKey(hexKey: string): Promise<CryptoKey> {
  const keyBuffer = hexToArrayBuffer(hexKey);

  return crypto.subtle.importKey('raw', keyBuffer, 'HKDF', false, ['deriveKey']);
}

/**
 * Derive a per-conversation AES-256-GCM key using HKDF.
 *
 * Uses HKDF-SHA256 with the conversation ID as salt and a fixed info string.
 * This produces a unique key per conversation while keeping the master key secret.
 *
 * Parameters match the server-side Node.js crypto.hkdf() implementation:
 * - Hash: SHA-256
 * - Salt: UTF-8 encoded conversationId
 * - Info: UTF-8 encoded "sos-encryption-v1"
 * - Output: 256-bit AES-GCM key
 */
export async function deriveConversationKey(
  masterKey: CryptoKey,
  conversationId: string
): Promise<CryptoKey> {
  const encoder = new TextEncoder();

  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      salt: encoder.encode(conversationId),
      info: encoder.encode(HKDF_INFO),
      hash: HKDF_HASH,
    },
    masterKey,
    { name: ENCRYPTION_ALGORITHM, length: KEY_LENGTH },
    false, // non-extractable
    ['encrypt', 'decrypt']
  );
}

// ==================== Encrypt / Decrypt ====================

/**
 * Encrypt a plaintext string using AES-256-GCM.
 *
 * Generates a random 12-byte IV per encryption (NIST recommendation for AES-GCM).
 * Returns the ciphertext (with appended 128-bit auth tag) and IV as Base64 strings.
 */
export async function encryptPayload(
  key: CryptoKey,
  plaintext: string
): Promise<{ ciphertext: string; iv: string }> {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const cipherBuffer = await crypto.subtle.encrypt(
    { name: ENCRYPTION_ALGORITHM, iv },
    key,
    encoder.encode(plaintext)
  );

  return {
    ciphertext: base64Encode(cipherBuffer),
    iv: base64Encode(iv.buffer),
  };
}

/**
 * Decrypt an AES-256-GCM ciphertext back to a plaintext string.
 *
 * The ciphertext must include the 128-bit auth tag appended by the encrypt operation.
 * Throws if the key is wrong, data is tampered, or the IV doesn't match.
 */
export async function decryptPayload(
  key: CryptoKey,
  ciphertext: string,
  iv: string
): Promise<string> {
  const decoder = new TextDecoder();

  const plainBuffer = await crypto.subtle.decrypt(
    { name: ENCRYPTION_ALGORITHM, iv: new Uint8Array(base64Decode(iv)) },
    key,
    base64Decode(ciphertext)
  );

  return decoder.decode(plainBuffer);
}
